import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, CalendarDays, Truck, Check, AlertCircle, ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import { Calendar } from "./ui/calendar";
import { Button } from "./ui/button";
import { useBooking } from "../contexts/BookingContext";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Swipeable image hook for touch + mouse drag
function useSwipe(imageCount, currentImg, setCurrentImg) {
  const ref = useRef(null);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const threshold = 50;

  const handleStart = useCallback((clientX) => { startX.current = clientX; isDragging.current = true; }, []);
  const handleEnd = useCallback((clientX) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = startX.current - clientX;
    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentImg < imageCount - 1) setCurrentImg(currentImg + 1);
      else if (diff < 0 && currentImg > 0) setCurrentImg(currentImg - 1);
    }
  }, [currentImg, imageCount, setCurrentImg]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTouchStart = (e) => handleStart(e.touches[0].clientX);
    const onTouchEnd = (e) => handleEnd(e.changedTouches[0].clientX);
    const onMouseDown = (e) => { e.preventDefault(); handleStart(e.clientX); };
    const onMouseUp = (e) => handleEnd(e.clientX);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [handleStart, handleEnd]);

  return ref;
}

/**
 * QuickView - Preview panel
 * 
 * NO duration toggle here - duration is ONLY selected on PDP
 * Shows event date and return date (if duration was previously selected on PDP)
 * 
 * CTA Buttons based on availability:
 * - Available: "Book Now" → Opens PDP
 * - Limited: "View Details" + "Available for shorter rental"
 * - Unavailable: "View Details" + "Next available from [Date]"
 */
export default function QuickView({ outfit, isOpen, onClose }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [addingToCart, setAddingToCart] = useState(false);
  const { 
    eventDate, 
    setEventDate, 
    rentalDuration,
    returnDate,
    hasDateSelected,
    hasFullBookingInfo,
    openDateModal 
  } = useBooking();
  
  const [checking, setChecking] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null); // 'available', 'limited', 'unavailable'
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [localDate, setLocalDate] = useState(eventDate);
  const [config, setConfig] = useState(null);
  const [currentImg, setCurrentImg] = useState(0);

  const images = outfit?.images?.length > 0 ? outfit.images : [];
  const desktopSwipeRef = useSwipe(images.length, currentImg, setCurrentImg);
  const mobileSwipeRef = useSwipe(images.length, currentImg, setCurrentImg);

  useEffect(() => {
    fetch(`${API}/settings/quickview`).then(r => r.json()).then(d => setConfig(d.config)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen && outfit && eventDate) {
      checkAvailability(eventDate);
    }
    if (isOpen) {
      setCurrentImg(0);
      setShowDatePicker(false);
      setLocalDate(eventDate);
      setAvailabilityStatus(null);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen, outfit?.id, eventDate]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  /**
   * Check availability for both 4 and 8 days
   * Determine status based on whether duration was previously selected
   */
  const checkAvailability = async (date) => {
    if (!date || !outfit) return;
    setChecking(true);
    try {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;
      
      // Check both durations
      const [res4, res8] = await Promise.all([
        axios.get(`${API}/outfits/check-availability?event_date=${dateStr}&rental_days=4`),
        axios.get(`${API}/outfits/check-availability?event_date=${dateStr}&rental_days=8`)
      ]);
      
      const avail4 = res4.data.availability?.[outfit.id] !== false;
      const avail8 = res8.data.availability?.[outfit.id] !== false;
      
      // Determine status
      if (rentalDuration) {
        // Duration was selected on PDP - check specific duration
        if (rentalDuration === 8) {
          if (avail8) setAvailabilityStatus('available');
          else if (avail4) setAvailabilityStatus('limited'); // 8 not available, but 4 is
          else setAvailabilityStatus('unavailable');
        } else {
          // 4-day selected
          setAvailabilityStatus(avail4 ? 'available' : 'unavailable');
        }
      } else {
        // No duration selected - just check if either is available
        setAvailabilityStatus((avail4 || avail8) ? 'available' : 'unavailable');
      }
    } catch {
      setAvailabilityStatus(null);
    } finally {
      setChecking(false);
    }
  };

  const handleViewDetails = () => {
    onClose();
    navigate(`/outfit/${outfit.id}`);
  };

  const handleQuickAdd = async () => {
    if (!hasDateSelected) return;
    setAddingToCart(true);
    try {
      const dateStr = format(eventDate, 'yyyy-MM-dd');
      await addToCart({
        outfit_id: outfit.id,
        event_date: dateStr,
        duration: rentalDuration,
        delivery_method: "pickup",
      });
      toast.success(`${outfit.name} added to cart`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleDateConfirm = () => {
    if (!localDate) return;
    setEventDate(localDate);
    setShowDatePicker(false);
    checkAvailability(localDate);
  };

  const handleChangeDate = () => {
    setLocalDate(eventDate);
    setShowDatePicker(true);
  };

  if (!isOpen || !outfit) return null;

  const highlights = outfit.best_suited_for || [];
  const c = config || {};
  const dispatchNote = c.dispatch_note || "Your outfit arrives 1–2 days before your event date.";
  const highlightsLabel = c.highlights_label || "Perfect For";
  
  // Compute display price
  const displayPrice = (() => {
    const prices = [outfit.price_4_day, outfit.price_8_day, outfit.price_per_day, ...(outfit.rental_options || []).flatMap(o => [o.price_4_day, o.price_8_day, o.price_per_day])].filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  })();

  const today = startOfToday();
  const minDate = addDays(today, 3);

  // CTA button and text based on availability
  const renderCTA = () => {
    if (!hasDateSelected) {
      return (
        <Button 
          onClick={handleViewDetails}
          className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 text-[10px] tracking-[0.15em] uppercase"
          data-testid="quickview-view-details-btn"
        >
          View Details
        </Button>
      );
    }
    
    if (checking) {
      return (
        <Button disabled className="w-full bg-[#9A7B4F]/50 text-white py-4 text-[10px] tracking-[0.15em] uppercase">
          Checking...
        </Button>
      );
    }
    
    if (availabilityStatus === 'available') {
      return (
        <div className="space-y-2">
          <Button 
            onClick={handleViewDetails}
            className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 text-[10px] tracking-[0.15em] uppercase"
            data-testid="quickview-book-btn"
          >
            Book Now
          </Button>
          {outfit.quick_add_enabled && (
            <Button
              onClick={handleQuickAdd}
              disabled={addingToCart}
              variant="outline"
              className="w-full border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F]/10 py-3 text-[10px] tracking-[0.15em] uppercase"
              data-testid="quickview-quick-add-btn"
            >
              <ShoppingBag className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
              {addingToCart ? "Adding..." : "Quick Add to Cart"}
            </Button>
          )}
        </div>
      );
    }
    
    if (availabilityStatus === 'limited') {
      return (
        <div className="space-y-2">
          <Button 
            onClick={handleViewDetails}
            className="w-full bg-[#D4A574] hover:bg-[#C49564] text-white py-4 text-[10px] tracking-[0.15em] uppercase"
            data-testid="quickview-limited-btn"
          >
            View Details
          </Button>
          <p className="text-[10px] text-center text-[#D4A574]">Available for shorter rental</p>
        </div>
      );
    }
    
    // Unavailable
    return (
      <div className="space-y-2">
        <Button 
          onClick={handleViewDetails}
          variant="outline"
          className="w-full border-[#282828]/20 text-[#676664] py-4 text-[10px] tracking-[0.15em] uppercase"
          data-testid="quickview-unavailable-btn"
        >
          View Details
        </Button>
        <p className="text-[10px] text-center text-[#8B4513]">Unavailable for selected dates</p>
      </div>
    );
  };

  // Availability indicator
  const renderAvailabilityIndicator = () => {
    if (!hasDateSelected || checking) return null;
    
    if (availabilityStatus === 'available') {
      return (
        <div className="flex items-center gap-2 text-[#4A7C59]" data-testid="quickview-available">
          <Check className="w-4 h-4" strokeWidth={1.5} />
          <p className="text-[12px] font-light">Available for your dates</p>
        </div>
      );
    }
    
    if (availabilityStatus === 'limited') {
      return (
        <div className="flex items-center gap-2 text-[#D4A574]" data-testid="quickview-limited">
          <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
          <p className="text-[12px] font-light">Limited availability</p>
        </div>
      );
    }
    
    if (availabilityStatus === 'unavailable') {
      return (
        <div className="flex items-center gap-2 text-[#8B4513]" data-testid="quickview-unavailable">
          <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
          <p className="text-[12px] font-light">Unavailable for selected dates</p>
        </div>
      );
    }
    
    return null;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/25 z-50" onClick={onClose} data-testid="quickview-backdrop" />

      {/* DESKTOP */}
      <div className="fixed z-50 hidden md:flex top-0 right-0 h-screen w-[58%] lg:w-[50%] xl:w-[44%] bg-[#FAF8F0]" data-testid="quickview-panel">
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white transition-colors" data-testid="quickview-close">
          <X className="w-4 h-4 text-[#282828]" strokeWidth={1.5} />
        </button>

        {/* Image */}
        <div className="w-[58%] flex-shrink-0 relative group bg-[#f5f3f0] select-none cursor-grab active:cursor-grabbing" ref={desktopSwipeRef} data-testid="quickview-image-swipe">
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              alt={outfit.name}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
              style={{ opacity: i === currentImg ? 1 : 0, objectPosition: `${(outfit.image_focal_points || {})[img]?.x ?? 50}% ${(outfit.image_focal_points || {})[img]?.y ?? 50}%` }}
            />
          ))}
          {images.length > 1 && (
            <>
              <button onClick={() => setCurrentImg(i => Math.max(0, i - 1))} className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 hover:bg-white flex items-center justify-center transition-all ${currentImg === 0 ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`} data-testid="quickview-img-prev"><ChevronLeft className="w-4 h-4 text-[#282828]" /></button>
              <button onClick={() => setCurrentImg(i => Math.min(images.length - 1, i + 1))} className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 hover:bg-white flex items-center justify-center transition-all ${currentImg === images.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`} data-testid="quickview-img-next"><ChevronRight className="w-4 h-4 text-[#282828]" /></button>
            </>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-10">
              {images.map((_, i) => (
                <button key={i} onClick={() => setCurrentImg(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentImg ? "bg-[#9A7B4F] w-4" : "bg-[#282828]/15"}`} />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto">
          <div className="flex-1 px-5 pt-5 pb-4 flex flex-col">
            <div className="mb-4">
              <h2 className="text-xl text-[#1A1A1A] font-medium leading-tight" data-testid="quickview-name">{outfit.name}</h2>
              <p className="text-[13px] text-[#5A5A5A] mt-2">
                From <span className="text-xl text-[#9A7B4F] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>${displayPrice}</span>
              </p>
            </div>

            {highlights.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] tracking-[0.12em] uppercase text-[#7A7A7A] mb-2 font-medium">{highlightsLabel}</p>
                <ul className="space-y-1.5">
                  {highlights.slice(0, 5).map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-[#4A4A4A]">
                      <span className="w-1.5 h-1.5 bg-[#9A7B4F] rounded-full mt-1.5 flex-shrink-0" />{h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex-1" />

            {!showDatePicker ? (
              <div className="space-y-3">
                {hasDateSelected ? (
                  <>
                    <div className="bg-white p-3 border border-[#282828]/10 space-y-2">
                      <div className="flex items-center gap-2 text-[10px] tracking-[0.1em] uppercase text-[#7A7A7A] font-medium">
                        <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />Your Event
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[#1A1A1A] font-medium text-[15px]">{format(eventDate, "EEEE, MMM d")}</p>
                          {hasFullBookingInfo && returnDate && (
                            <p className="text-[11px] text-[#676664]/60 mt-0.5">
                              {rentalDuration}-day rental · Returns {format(returnDate, "MMM d")}
                            </p>
                          )}
                        </div>
                        <button 
                          onClick={handleChangeDate} 
                          className="text-[11px] text-[#9A7B4F] hover:underline underline-offset-2"
                          data-testid="quickview-change-date"
                        >
                          Change
                        </button>
                      </div>
                      <div className="flex items-start gap-1.5 pt-2 border-t border-[#282828]/5">
                        <Truck className="w-3 h-3 text-[#9A7B4F] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <p className="text-[9px] text-[#676664]/50 font-light leading-relaxed">{dispatchNote}</p>
                      </div>
                    </div>
                    
                    {renderAvailabilityIndicator()}
                  </>
                ) : (
                  <div className="flex items-center gap-2 py-1">
                    <CalendarDays className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                    <p className="text-[12px] text-[#676664]/50 font-light">Select your event date to check availability</p>
                  </div>
                )}
                
                {renderCTA()}
              </div>
            ) : (
              <div className="space-y-3" data-testid="quickview-date-picker">
                <p className="text-[10px] tracking-[0.15em] uppercase text-[#676664]/40">Select Event Date</p>
                <div className="flex justify-center">
                  <Calendar 
                    mode="single" 
                    selected={localDate} 
                    onSelect={setLocalDate} 
                    disabled={(date) => isBefore(date, minDate)} 
                    className="border border-[#282828]/8 bg-white"
                    classNames={{
                      day_selected: "bg-[#9A7B4F] text-white hover:bg-[#8a6b3f]",
                    }}
                  />
                </div>
                {localDate && (
                  <div className="bg-white p-2.5 border border-[#282828]/8 text-[11px]">
                    <div className="flex justify-between">
                      <span className="text-[#676664]/50">Event Date</span>
                      <span className="text-[#282828] font-medium">{format(localDate, "EEE, MMM d")}</span>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={() => setShowDatePicker(false)} variant="outline" className="flex-1 border-[#282828]/15 text-[#676664] text-[10px] tracking-[0.12em] uppercase">Cancel</Button>
                  <Button onClick={handleDateConfirm} disabled={!localDate} className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.12em] uppercase disabled:opacity-40" data-testid="quickview-confirm-date">Confirm</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE */}
      <div className="fixed z-50 bg-[#FAF8F0] md:hidden inset-0 overflow-y-auto" data-testid="quickview-panel-mobile">
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center bg-white/80" data-testid="quickview-close-mobile">
          <X className="w-4 h-4 text-[#282828]" strokeWidth={1.5} />
        </button>
        {images.length > 0 && (
          <div className="relative bg-[#f5f3f0] select-none" style={{ height: "55vh" }} ref={mobileSwipeRef} data-testid="quickview-mobile-image-swipe">
            {images.map((img, i) => (
              <img key={i} src={img} alt={outfit.name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{ opacity: i === currentImg ? 1 : 0, objectPosition: `${(outfit.image_focal_points || {})[img]?.x ?? 50}% ${(outfit.image_focal_points || {})[img]?.y ?? 50}%` }} />
            ))}
            {images.length > 1 && (
              <>
                <button onClick={() => setCurrentImg(i => Math.max(0, i - 1))} className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 flex items-center justify-center ${currentImg === 0 ? 'opacity-30' : ''}`} data-testid="quickview-mobile-img-prev"><ChevronLeft className="w-4 h-4 text-[#282828]" /></button>
                <button onClick={() => setCurrentImg(i => Math.min(images.length - 1, i + 1))} className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 flex items-center justify-center ${currentImg === images.length - 1 ? 'opacity-30' : ''}`} data-testid="quickview-mobile-img-next"><ChevronRight className="w-4 h-4 text-[#282828]" /></button>
              </>
            )}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setCurrentImg(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentImg ? "bg-[#9A7B4F] w-4" : "bg-[#282828]/15"}`} />
                ))}
              </div>
            )}
          </div>
        )}
        <div className="px-5 py-5 space-y-4">
          <div>
            <h2 className="text-lg text-[#1A1A1A] font-medium">{outfit.name}</h2>
            <p className="text-[13px] text-[#5A5A5A] mt-2">
              From <span className="text-lg text-[#9A7B4F] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>${displayPrice}</span>
            </p>
          </div>
          
          {highlights.length > 0 && (
            <div>
              <p className="text-[11px] tracking-[0.12em] uppercase text-[#7A7A7A] mb-2 font-medium">{highlightsLabel}</p>
              <ul className="space-y-1.5">
                {highlights.slice(0, 5).map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-[#4A4A4A]">
                    <span className="w-1.5 h-1.5 bg-[#9A7B4F] rounded-full mt-1.5 flex-shrink-0" />{h}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {hasDateSelected ? (
            <div className="bg-white p-3 border border-[#282828]/10 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#1A1A1A] font-medium text-[15px]">{format(eventDate, "EEE, MMM d")}</p>
                  {hasFullBookingInfo && returnDate && (
                    <p className="text-[11px] text-[#5A5A5A]">{rentalDuration}d rental · Returns {format(returnDate, "MMM d")}</p>
                  )}
                </div>
                <button onClick={handleChangeDate} className="text-[11px] text-[#9A7B4F] font-medium">Change</button>
              </div>
              <div className="flex items-start gap-1.5 pt-2 border-t border-[#282828]/5">
                <Truck className="w-3.5 h-3.5 text-[#9A7B4F] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-[10px] text-[#5A5A5A]">{dispatchNote}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 py-1">
              <CalendarDays className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
              <p className="text-[13px] text-[#5A5A5A]">Select event date to check availability</p>
            </div>
          )}
          
          {renderAvailabilityIndicator()}
          {renderCTA()}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        [data-testid="quickview-panel"] { animation: slideInRight 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        [data-testid="quickview-panel-mobile"] { animation: slideInUp 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </>
  );
}
